import { v4 as uuidv4 } from 'uuid';
import api from '../../../app/api/axios-api';

export type ReviewerGovernanceKey = 'owasp' | 'ontology' | 'internal_compliance' | 'external_compliance';

export const GOVERNANCE_RESULT_ALIASES: Record<ReviewerGovernanceKey, string[]> = {
  owasp: ['owasp_mlp', 'owasp'],
  ontology: ['ontology'],
  internal_compliance: ['internal_compliance'],
  external_compliance: ['external_compliance']
};

const PRIMARY_RESULT_KEY: Record<ReviewerGovernanceKey, string> = {
  owasp: 'owasp_mlp',
  ontology: 'ontology',
  internal_compliance: 'internal_compliance',
  external_compliance: 'external_compliance'
};

const isNonEmptyGovernanceValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
};

import { getReviewerStudioResults, normalizeReviewerStudioResults } from './governanceResultsAccess';

/** `results` blob from combined LLM analysis API (supports nested and legacy Mongo shapes). */
export const extractCombinedLlmAnalysisResults = (payload: unknown): Record<string, unknown> | null => {
  const normalized = normalizeReviewerStudioResults(payload);
  return Object.keys(normalized).length > 0 ? normalized : null;
};

export const fetchCombinedLlmAnalysis = async (projectId: string): Promise<Record<string, unknown>> => {
  const response = await api.get(`/project/features/${projectId}/combined-llm-analysis`);
  const data = response.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return {};
};

/** True when Redux already has all four governance entries for this project (skip combined LLM GET). */
export const hasCachedReviewerGovernance = (projectResults: unknown, projectId: string): boolean => {
  if (!projectResults || typeof projectResults !== 'object') return false;

  const root = projectResults as Record<string, unknown>;
  const stateProjectId =
    (typeof root.projectId === 'string' && root.projectId) ||
    (typeof (root.project as Record<string, unknown> | undefined)?.projectId === 'string' &&
      String((root.project as Record<string, unknown>).projectId)) ||
    (typeof (root.combined_llm_analysis as Record<string, unknown> | undefined)?.projectId === 'string' &&
      String((root.combined_llm_analysis as Record<string, unknown>).projectId));

  if (stateProjectId && stateProjectId !== projectId) return false;

  const bucket = getReviewerStudioResults(projectResults);
  return getMissingReviewerGovernanceKeys(bucket).length === 0;
};

export const getMissingReviewerGovernanceKeys = (
  combinedLlmResults: Record<string, unknown> | null | undefined
): ReviewerGovernanceKey[] => {
  const bucket = combinedLlmResults && typeof combinedLlmResults === 'object' ? combinedLlmResults : null;
  if (!bucket) {
    return Object.keys(GOVERNANCE_RESULT_ALIASES) as ReviewerGovernanceKey[];
  }

  return (Object.keys(GOVERNANCE_RESULT_ALIASES) as ReviewerGovernanceKey[]).filter(key => {
    const aliases = GOVERNANCE_RESULT_ALIASES[key];
    return !aliases.some(alias => isNonEmptyGovernanceValue(bucket[alias]));
  });
};

const unwrapApiPayload = (response: unknown): Record<string, unknown> | null => {
  if (!response || typeof response !== 'object') return null;
  const root = response as Record<string, unknown>;
  if (root.data && typeof root.data === 'object' && !Array.isArray(root.data)) {
    return root.data as Record<string, unknown>;
  }
  if (root.result && typeof root.result === 'object' && !Array.isArray(root.result)) {
    return root.result as Record<string, unknown>;
  }
  return root;
};

/** Pull governance fragment from a feature POST response body. */
export const extractGovernanceFragmentFromApiResponse = (
  key: ReviewerGovernanceKey,
  response: unknown
): unknown | null => {
  const sources = [unwrapApiPayload(response)];
  const aliases = GOVERNANCE_RESULT_ALIASES[key];

  for (const source of sources) {
    if (!source) continue;
    for (const alias of aliases) {
      if (source[alias] != null) return source[alias];
    }
    if (key === 'owasp' && (source.controls || source.overall_status)) return source;
    if (key === 'ontology' && (source.domains || source.overall_risk)) return source;
    if (key === 'internal_compliance' && source.internal_compliance != null) return source;
    if (key === 'external_compliance' && source.external_compliance != null) return source;
  }

  return null;
};

const appendGovernanceFragment = (
  results: Record<string, unknown>,
  key: ReviewerGovernanceKey,
  fragment: unknown
): Record<string, unknown> => {
  if (fragment == null) return results;

  const next = { ...results };
  const primaryKey = PRIMARY_RESULT_KEY[key];
  const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : [value]);
  const existing = next[primaryKey];
  const merged = existing != null ? [...toArray(existing), ...toArray(fragment)] : toArray(fragment);
  next[primaryKey] = merged;

  if (key === 'owasp') {
    next.owasp = merged;
  }

  return next;
};

export interface EnsureReviewerGovernanceOptions {
  projectId: string;
  userId: string;
  title?: string;
  combinedLlmPayload: Record<string, unknown>;
  combinedLlmResults?: Record<string, unknown> | null;
  onStatusUpdate?: (status: string) => void;
}

export interface EnsureReviewerGovernanceResult {
  called: ReviewerGovernanceKey[];
  skipped: boolean;
  failed: ReviewerGovernanceKey[];
  /** Combined LLM analysis payload with governance appended under `results`. */
  updatedCombinedPayload: Record<string, unknown>;
}

/**
 * Call missing governance feature APIs and append each response into project `results`.
 */
export const ensureReviewerGovernanceResults = async (
  options: EnsureReviewerGovernanceOptions
): Promise<EnsureReviewerGovernanceResult> => {
  const baseResults =
    options.combinedLlmResults ??
    extractCombinedLlmAnalysisResults(options.combinedLlmPayload) ??
    ({} as Record<string, unknown>);

  let mergedResults = { ...baseResults };
  const missing = getMissingReviewerGovernanceKeys(mergedResults);

  const buildPayload = (): Record<string, unknown> => ({
    ...options.combinedLlmPayload,
    results: mergedResults
  });

  if (missing.length === 0) {
    return { called: [], skipped: true, failed: [], updatedCombinedPayload: buildPayload() };
  }

  const meaid = uuidv4();
  const basePayload = {
    projectId: options.projectId,
    userId: options.userId,
    meaid,
    is_rerun: true
  };
  const title = options.title || 'Project';
  const failed: ReviewerGovernanceKey[] = [];

  const applyResponse = (key: ReviewerGovernanceKey, response: unknown) => {
    const fragment = extractGovernanceFragmentFromApiResponse(key, response);
    if (fragment != null) {
      mergedResults = appendGovernanceFragment(mergedResults, key, fragment);
    }
  };

  const runOntology = async () => {
    options.onStatusUpdate?.('Generating Ontology');
    const response = await api.post('/ontology/project/features/ontology', basePayload);
    applyResponse('ontology', response.data);
  };

  const runOwasp = async () => {
    options.onStatusUpdate?.('Generating OWASP Controls');
    const response = await api.post('/project/features/owasp', basePayload);
    applyResponse('owasp', response.data);
  };

  const parallelKeys: ReviewerGovernanceKey[] = [];
  if (missing.includes('ontology')) parallelKeys.push('ontology');
  if (missing.includes('owasp')) parallelKeys.push('owasp');

  if (parallelKeys.length > 0) {
    const results = await Promise.allSettled(
      parallelKeys.map(key => (key === 'ontology' ? runOntology() : runOwasp()))
    );
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        const key = parallelKeys[index];
        failed.push(key);
        console.error(`${key} generation failed:`, result.reason);
      }
    });
  }

  if (missing.includes('internal_compliance')) {
    options.onStatusUpdate?.('Generating Internal Compliance');
    try {
      const response = await api.post('/project/features/internal-compliance', { ...basePayload, title });
      applyResponse('internal_compliance', response.data);
    } catch (error) {
      failed.push('internal_compliance');
      console.error('Internal compliance generation failed:', error);
    }
  }

  if (missing.includes('external_compliance')) {
    options.onStatusUpdate?.('Generating External Compliance');
    try {
      const response = await api.post('/project/features/external-compliance', { ...basePayload, title });
      applyResponse('external_compliance', response.data);
    } catch (error) {
      failed.push('external_compliance');
      console.error('External compliance generation failed:', error);
    }
  }

  return {
    called: missing,
    skipped: false,
    failed,
    updatedCombinedPayload: buildPayload()
  };
};
